import { useState, useEffect } from "react";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  ScrollArea,
} from "@repo/ui";
import {
  LogOut,
  FileText,
  Shield,
  Download,
  ArrowRight,
  Loader2,
  Check,
} from "lucide-react";
import { useAuth } from "@/lib/auth/auth-provider";
import { logout } from "@/lib/api/auth";
import {
  getLegalStatus,
  acceptLegalDocuments,
  getBaaDownloadUrl,
} from "@/lib/api/legal";
import { TermsOfServiceContent } from "@/components/legal/terms-of-service-content";
import { PrivacyPolicyContent } from "@/components/legal/privacy-policy-content";
import { PdfViewer } from "@/components/legal/pdf-viewer";

export const Route = createFileRoute("/_auth/accept-terms")({
  component: AcceptTermsPage,
});

type Step = "tos" | "privacy" | "baa";

const STEPS: Step[] = ["tos", "privacy", "baa"];

function AcceptTermsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, needsLegalAcceptance, refetch } =
    useAuth();

  const [currentStep, setCurrentStep] = useState<Step>("tos");
  const [accepted, setAccepted] = useState({
    tos: false,
    privacy: false,
    baa: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [baaUrl, setBaaUrl] = useState<string | null>(null);
  const [error, setError] = useState("");

  const stepIndex = STEPS.indexOf(currentStep);
  const isLastStep = currentStep === "baa";

  // Fetch BAA PDF URL when entering the BAA step
  useEffect(() => {
    if (currentStep !== "baa" || baaUrl) return;

    async function fetchBaaUrl() {
      try {
        const { url } = await getBaaDownloadUrl();
        setBaaUrl(url);
      } catch {
        setError("Failed to load BAA document. Please try again.");
      }
    }
    fetchBaaUrl();
  }, [currentStep, baaUrl]);

  // Auth guard — must be after all hooks
  if (isLoading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!needsLegalAcceptance) {
    return <Navigate to="/" />;
  }

  function handleAcceptAndNext() {
    if (isLastStep) {
      handleSubmitAll();
    } else {
      setCurrentStep(STEPS[stepIndex + 1]!);
    }
  }

  const handleSubmitAll = async () => {
    setError("");
    setIsSubmitting(true);

    try {
      const status = await getLegalStatus();

      const acceptances = status.pendingDocuments.map((doc) => ({
        documentType: doc.type,
        documentVersion: doc.currentVersion,
      }));

      if (acceptances.length === 0) {
        await refetch();
        navigate({ to: "/" });
        return;
      }

      await acceptLegalDocuments(acceptances);
      await refetch();
      navigate({ to: "/" });
    } catch (err) {
      const apiError = err as {
        statusCode?: number;
        error?: string;
        message?: string;
      };
      setError(
        apiError.message ||
          apiError.error ||
          "Something went wrong. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadBaa = async () => {
    setIsDownloading(true);
    try {
      const { url } = await getBaaDownloadUrl();
      window.open(url, "_blank");
    } catch (err) {
      const apiError = err as { message?: string };
      setError(apiError.message || "Failed to download BAA. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      sessionStorage.clear();
      navigate({ to: "/login" });
    } catch {
      navigate({ to: "/login" });
    }
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 flex h-16 items-center justify-between bg-surface px-4 md:px-6 mx-2 mt-2 rounded-xl shadow-sm">
        <div className="flex items-center">
          <img
            src="/logo-with-text-blue.png"
            alt="C2 Vault"
            className="h-7 w-auto"
          />
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="text-muted-foreground hover:text-destructive"
        >
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline ml-1.5">Log out</span>
        </Button>
      </nav>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        <div className="relative px-4 py-12 sm:py-16">
          {/* Error toast */}
          {error && (
            <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl border border-destructive/50 bg-destructive/10 text-destructive text-sm shadow-lg backdrop-blur-sm">
              {error}
            </div>
          )}

          <div className="mx-auto max-w-2xl">
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {STEPS.map((step, i) => {
                const isCompleted = i < stepIndex;
                const isCurrent = step === currentStep;

                return (
                  <div key={step} className="flex items-center gap-2">
                    {i > 0 && (
                      <div
                        className={`h-px w-8 sm:w-12 ${isCompleted ? "bg-primary" : "bg-border"}`}
                      />
                    )}
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                        isCompleted
                          ? "bg-primary text-primary-foreground"
                          : isCurrent
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {isCompleted ? <Check className="h-4 w-4" /> : i + 1}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ToS step */}
            {currentStep === "tos" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <FileText className="h-6 w-6 text-primary" />
                    <div>
                      <CardTitle className="text-xl">Terms of Use</CardTitle>
                      <CardDescription>
                        Please read the Terms of Use before continuing.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ScrollArea className="h-80 rounded-md border bg-muted/30 p-4">
                    <TermsOfServiceContent />
                  </ScrollArea>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="tos"
                      checked={accepted.tos}
                      onCheckedChange={(checked) =>
                        setAccepted((prev) => ({
                          ...prev,
                          tos: checked === true,
                        }))
                      }
                    />
                    <label
                      htmlFor="tos"
                      className="text-sm text-foreground cursor-pointer select-none"
                    >
                      I have read and agree to the Terms of Use
                    </label>
                  </div>

                  <Button
                    className="w-full"
                    size="lg"
                    disabled={!accepted.tos}
                    onClick={handleAcceptAndNext}
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Privacy Policy step */}
            {currentStep === "privacy" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Shield className="h-6 w-6 text-primary" />
                    <div>
                      <CardTitle className="text-xl">Privacy Policy</CardTitle>
                      <CardDescription>
                        Please read the Privacy Policy before continuing.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ScrollArea className="h-80 rounded-md border bg-muted/30 p-4">
                    <PrivacyPolicyContent />
                  </ScrollArea>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="privacy"
                      checked={accepted.privacy}
                      onCheckedChange={(checked) =>
                        setAccepted((prev) => ({
                          ...prev,
                          privacy: checked === true,
                        }))
                      }
                    />
                    <label
                      htmlFor="privacy"
                      className="text-sm text-foreground cursor-pointer select-none"
                    >
                      I have read and agree to the Privacy Policy
                    </label>
                  </div>

                  <Button
                    className="w-full"
                    size="lg"
                    disabled={!accepted.privacy}
                    onClick={handleAcceptAndNext}
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* BAA step */}
            {currentStep === "baa" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-6 w-6 text-secondary" />
                      <div>
                        <CardTitle className="text-xl">
                          Business Associate Agreement
                        </CardTitle>
                        <CardDescription>
                          Required under HIPAA. Please review the BAA before
                          accepting.
                        </CardDescription>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadBaa}
                      disabled={isDownloading}
                      className="shrink-0"
                    >
                      {isDownloading ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Download
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {baaUrl ? (
                    <PdfViewer url={baaUrl} />
                  ) : (
                    <div className="flex items-center justify-center py-16 rounded-md border bg-muted/30">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-sm text-muted-foreground">
                        Loading document...
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="baa"
                      checked={accepted.baa}
                      onCheckedChange={(checked) =>
                        setAccepted((prev) => ({
                          ...prev,
                          baa: checked === true,
                        }))
                      }
                    />
                    <label
                      htmlFor="baa"
                      className="text-sm text-foreground cursor-pointer select-none"
                    >
                      I have read and agree to the Business Associate Agreement
                    </label>
                  </div>

                  <Button
                    className="w-full"
                    size="lg"
                    disabled={!accepted.baa || isSubmitting}
                    onClick={handleAcceptAndNext}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      "Accept All & Continue"
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    By accepting, you confirm that you are authorized to enter
                    into these agreements on behalf of your pharmacy.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
